import React from 'react';
import { format } from 'date-fns';
import { Tag, Ticket, Info } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import InlineDescriptionCell from './InlineDescriptionCell';
import InlineEditCell from './InlineEditCell';

export default function InvoiceQueueTable({
  invoices,
  selectedIds,
  onSelectAll,
  onSelectOne,
  onDescriptionUpdate,
  onEmailUpdate,
  onHoursUpdate,
  onDiscountClick,
  loading,
}) {
  const allSelected = invoices.length > 0 && selectedIds.length === invoices.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < invoices.length;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try { return format(new Date(dateStr), 'MM/dd/yyyy'); } catch { return dateStr; }
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-3">
        {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="p-12 text-center">
        <p className="text-muted-foreground text-sm">No pending invoice items</p>
        <p className="text-muted-foreground text-xs mt-1">
          Items will appear here when ClickUp webhooks are received.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto" data-testid="invoice-items-table">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-[44px] pl-4 sm:pl-6">
              <Checkbox
                checked={allSelected}
                ref={(el) => {
                  if (el) {
                    const checkboxEl = el.closest('button');
                    if (checkboxEl) checkboxEl.dataset.indeterminate = someSelected ? 'true' : 'false';
                  }
                }}
                onCheckedChange={onSelectAll}
                aria-label="Select all invoice items"
                data-testid="invoice-table-select-all-checkbox"
              />
            </TableHead>
            <TableHead className="w-[120px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Date</TableHead>
            <TableHead className="min-w-[140px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Client</TableHead>
            <TableHead className="min-w-[120px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Staff</TableHead>
            <TableHead className="min-w-[200px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Client Email</TableHead>
            <TableHead className="w-[100px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Hours</TableHead>
            <TableHead className="w-[120px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Amount</TableHead>
            <TableHead className="w-[90px] text-xs font-semibold uppercase tracking-wide text-muted-foreground"><s>$</s></TableHead>
            <TableHead className="min-w-[220px] max-w-[280px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <TooltipProvider><Tooltip><TooltipTrigger className="inline-flex items-center gap-1">
                Original Description <Info className="h-3 w-3" />
              </TooltipTrigger><TooltipContent>Untouched log from ClickUp. Use as reference for the sanitized version.</TooltipContent></Tooltip></TooltipProvider>
            </TableHead>
            <TableHead className="min-w-[280px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sanitized Description (to Invoice)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((invoice) => {
            const isSelected = selectedIds.includes(invoice.id);
            const staffNames = Array.isArray(invoice.staff_data)
              ? invoice.staff_data.map(s => s.username || s.email || '').filter(Boolean).join(', ')
              : '';

            return (
              <TableRow
                key={invoice.id}
                className={`transition-colors duration-150 ${
                  isSelected
                    ? 'bg-[var(--row-selected)] hover:bg-[var(--row-selected)]'
                    : 'hover:bg-[var(--row-hover)]'
                }`}
                data-testid={`invoice-row-${invoice.id}`}
              >
                <TableCell className="pl-4 sm:pl-6">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked) => onSelectOne(invoice.id, checked)}
                    aria-label={`Select invoice item ${invoice.id}`}
                    data-testid="invoice-row-select-checkbox"
                  />
                </TableCell>

                {/* Date */}
                <TableCell data-testid="invoice-table-date-cell">
                  <span className="text-sm" data-role="mono">{formatDate(invoice.created_at)}</span>
                </TableCell>

                {/* Client (linked to ClickUp) */}
                <TableCell data-testid="invoice-table-client-cell">
                  {invoice.client ? (
                    invoice.clickup_task_id && !invoice.clickup_task_id.startsWith('merged_') ? (
                      <a
                        href={`https://app.clickup.com/t/${invoice.clickup_task_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-primary hover:underline truncate block max-w-[180px]"
                        title={`${invoice.client} — Open in ClickUp`}
                      >
                        {invoice.client}
                      </a>
                    ) : (
                      <span className="text-sm font-medium truncate block max-w-[180px]" title={invoice.client}>
                        {invoice.client}
                      </span>
                    )
                  ) : <em className="text-sm text-muted-foreground">—</em>}
                </TableCell>

                {/* Staff */}
                <TableCell data-testid="invoice-table-staff-cell">
                  <span className="text-sm truncate block max-w-[140px]" title={staffNames}>
                    {staffNames || <em className="text-muted-foreground">—</em>}
                  </span>
                </TableCell>

                {/* Client Email — editable */}
                <TableCell data-testid="invoice-table-client-email-cell">
                  <InlineEditCell
                    value={invoice.client_email}
                    onSave={onEmailUpdate}
                    rowId={invoice.id}
                    type="email"
                    placeholder="No email"
                  />
                </TableCell>

                {/* Hours — editable */}
                <TableCell className="text-right" data-testid="invoice-table-hours-cell">
                  <InlineEditCell
                    value={invoice.hours}
                    onSave={onHoursUpdate}
                    rowId={invoice.id}
                    type="number"
                    placeholder="0.00"
                    className="justify-end"
                  />
                </TableCell>

                {/* Amount (auto-calculated) */}
                <TableCell className="text-right" data-testid="invoice-table-amount-cell">
                  <span className="text-sm font-semibold" data-role="mono">
                    {formatCurrency(invoice.display_amount != null ? invoice.display_amount : invoice.amount)}
                  </span>
                </TableCell>

                {/* Discount */}
                <TableCell>
                  {(() => {
                    const disc = invoice.discount || {};
                    const hasDisc = disc.type && disc.type !== 'none' && parseFloat(disc.value) > 0;
                    if (hasDisc) {
                      const label = disc.type === 'percentage' ? `${disc.value}%` : formatCurrency(disc.value);
                      return (
                        <button onClick={() => onDiscountClick && onDiscountClick(invoice)} className="cursor-pointer">
                          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 transition-colors">
                            <Tag className="h-3 w-3 mr-1" />{label}
                          </Badge>
                        </button>
                      );
                    }
                    return (
                      <button
                        onClick={() => onDiscountClick && onDiscountClick(invoice)}
                        className="text-muted-foreground hover:text-primary transition-colors"
                        title="Apply discount"
                      >
                        <Ticket className="h-4 w-4" />
                      </button>
                    );
                  })()}
                </TableCell>

                {/* Original Description — read-only */}
                <TableCell data-testid="invoice-table-original-desc-cell">
                  <span className="text-xs text-muted-foreground leading-relaxed block max-w-[280px]" title={invoice.original_description || ''}>
                    {(() => {
                      const text = invoice.original_description || '';
                      return text.length > 350 ? text.slice(0, 350) + '...' : (text || <em>—</em>);
                    })()}
                  </span>
                </TableCell>

                {/* Sanitized Description (to Invoice) — editable */}
                <TableCell data-testid="invoice-table-description-cell">
                  <InlineDescriptionCell
                    value={invoice.description}
                    onSave={onDescriptionUpdate}
                    rowId={invoice.id}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
